import React from 'react';

const StudentFilters = ({
  academicYears,
  completedAcademicYears,
  selectedAcademicYear,
  onAcademicYearChange,
  isSemesterBased,
  semesters,
  selectedSemester,
  onSemesterChange,
  terms,
  selectedTerm,
  onTermChange,
  classes,
  selectedClass,
  onClassChange,
  statusFilter,
  onStatusFilterChange,
  onClearFilters,
  isLoading
}) => {
  const hasActiveFilters = selectedClass || statusFilter || selectedTerm || selectedSemester;

  const allAcademicYears = [
    ...academicYears.map(year => ({ ...year, isCompleted: false })),
    ...completedAcademicYears.map(year => ({ ...year, isCompleted: true }))
  ];

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Filter Students</h3>
        <p className="text-gray-600 text-xs">Refine your student list using the filters below</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
        {/* Academic Year Filter */}
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Academic Year</label>
          <select
            value={selectedAcademicYear}
            onChange={(e) => onAcademicYearChange(e.target.value)}
            className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
            disabled={isLoading}
          >
            <option value="" disabled>Select Academic Year</option>
            {allAcademicYears.map((year) => (
              <option
                key={year.id || year._id}
                value={year.id || year._id}
                className={year.isCompleted ? 'text-gray-400 bg-gray-100' : ''}
              >
                {(year.displayName || year.name) + (year.isCompleted ? ' (Completed)' : '')}
              </option>
            ))}
          </select>
        </div>

        {/* Semester Filter */}
        {isSemesterBased && (
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">Semester</label>
            <select
              value={selectedSemester}
              onChange={e => onSemesterChange(e.target.value)}
              className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              disabled={isLoading || semesters.length === 0}
            >
              <option value="" disabled>Select Semester</option>
              {semesters.map(sem => (
                <option key={sem.id || sem._id} value={sem.id || sem._id}>
                  {sem.semesterName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Term Filter */}
        {!isSemesterBased && terms && terms.length > 0 && (
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">Term</label>
            <select
              value={selectedTerm}
              onChange={e => onTermChange(e.target.value)}
              className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              disabled={isLoading || terms.length === 0}
            >
              <option value="">All Terms</option>
              {terms.map(term => (
                <option key={term.id || term._id} value={term.id || term._id}>
                  {term.termName || term.termLabel || `Term ${term.termNumber}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Class Filter */}
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Class</label>
          <select
            value={selectedClass}
            onChange={e => onClassChange(e.target.value)}
            className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
            disabled={isLoading || (isSemesterBased && !selectedSemester) || (!isSemesterBased && !selectedTerm && classes.length === 0)}
          >
            <option value="">All Classes</option>
            {classes.map(cls => (
              <option key={cls.classId || cls._id} value={cls.classId || cls._id}>
                {cls.className}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter - This filters by authStatus now */}
        <div className="flex-1 min-w-[120px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={e => onStatusFilterChange(e.target.value)}
            className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
            disabled={isLoading}
          >
            <option value="">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="flex-shrink-0 mt-2 md:mt-0">
            <button
              onClick={onClearFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              disabled={isLoading}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Quick Stats / Tips */}
      <div className="mt-3">
        <ul className="text-xs text-gray-500 flex flex-wrap gap-x-6 gap-y-1">
          <li className="flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Select academic year to see relevant classes
          </li>
          <li className="flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Use status filter to find active/inactive students
          </li>
          {!isSemesterBased && (
            <li className="flex items-center">
              <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Filter by term for non-semester academic years
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default StudentFilters;